// @ts-check

import { deleteCustomer, getCustomers } from '@/api/customers'
import { OWNER_TYPE_COMPANY, OWNER_TYPE_SYSTEM } from '@/core/constants'
import {
  paginationGetters,
  paginationMutations,
  paginationState
} from '@/store/paginationHelpers'
import { queryGetters, queryMutations, queryState } from '@/store/queryHelpers'
import * as _ from 'lodash-es'

export const initCustomerState = () => ({
  customers: null,
  hasSomeCustomers: false,
  ...paginationState,
  ...queryState
})

/**
 * @typedef State
 * @type {Object}
 *
 * @property {Customers} customers
 * @property {boolean} hasSomeCustomers
 */

const companyType = 0

/** @type {import('vuex').MutationTree<any>} */
export const customerMutations = {
  ...paginationMutations,
  ...queryMutations,
  /**
   * @param {State} state
   */
  clearAllData: (state) => {
    Object.assign(state, initCustomerState())
  },

  /**
   * @param {State} state
   * @param {State['customers']} customers
   */
  setCustomers: (state, customers) => {
    state.customers = customers
    if (!state.hasSomeCustomers && customers?.length) {
      state.hasSomeCustomers = true
    }
  },

  /**
   * Removes the customer with the given id from the state.
   *
   * @param {State} state
   * @param {number|string} customerId
   */
  removeCustomer: (state, customerId) => {
    if (!state.customers) {
      return
    }

    state.customers = _.remove(
      state.customers,
      /** @param {Customers} customer */
      ({ id: targetId }) => targetId !== customerId
    )

    if (state?.customers.length === 0) {
      state.hasSomeCustomers = false
    }
  },
  removeCustomers: (state) => {
    state.customers = null
  }
}

/** @type {import('vuex').GetterTree<any, any>} */
export const customerGetters = {
  ...paginationGetters,
  ...queryGetters,
  getCustomer: (state, getters) => (id) => {
    const customer = getters.customers?.find((customer) => {
      return customer.id === id
    })

    if (!customer) {
      return null
    }

    return JSON.parse(JSON.stringify(customer))
  },
  customers: ({ customers }) => customers,
  getCustomersWithNameAndBid: (state) => {
    return (state.customers ?? []).map((customer) => {
      return {
        label:
          customer.type === companyType
            ? `${customer.company_name} (${customer.business_id})`
            : `${customer.first_name} ${customer.last_name}`,
        value: customer.id
      }
    })
  }
}

/** @type {import('vuex').ActionTree<any, any>} */
export const customerActions = {
  async getCustomers(
    { commit, getters },
    {
      page = undefined,
      reset = false,
      owner = [OWNER_TYPE_SYSTEM, OWNER_TYPE_COMPANY]
    } = {}
  ) {
    if (reset) {
      commit('removeCustomers')
    }

    const { data: customers } = await getCustomers(getters.params, page, owner)

    commit('setCustomers', customers)
  },

  async deleteCustomer({ commit }, id) {
    await deleteCustomer(id)
    commit('removeCustomer', id)
  }
}

export const customerModule = {
  namespaced: true,
  state: initCustomerState,
  actions: customerActions,
  mutations: customerMutations,
  getters: customerGetters
}

export default customerModule
